import { ChevronsUpDown, LogOut, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { useAuth } from "../../context/auth-context.js";
import { initialsOf } from "../../lib/utils.js";
import { UserRoleBadge } from "../roles/role-badge.js";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu.js";

export interface UserMenuItem {
  label: ReactNode;
  icon?: LucideIcon;
  onSelect: () => void;
}

export interface UserMenuProps {
  image?: string | null;
  items?: UserMenuItem[];
}

export const UserMenu = ({ image, items = [] }: UserMenuProps) => {
  const { data, signOut } = useAuth();
  if (!data) return null;
  const { user } = data;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="group flex w-full items-center gap-2.5 rounded-md p-2 text-left text-sm transition-colors hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring/40">
        <Avatar className="size-8">
          {image ? <AvatarImage src={image} alt={user.name ?? user.email} /> : null}
          <AvatarFallback>{initialsOf(user.name, user.email)}</AvatarFallback>
        </Avatar>
        <div className="flex flex-1 flex-col leading-tight">
          <span className="line-clamp-1 font-medium">{user.name ?? user.email}</span>
          <span className="line-clamp-1 text-xs text-muted-foreground">
            {user.email}
          </span>
        </div>
        <ChevronsUpDown className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="flex flex-col gap-1 text-foreground">
          <span className="text-sm font-medium">{user.name ?? user.email}</span>
          <span className="text-xs font-normal text-muted-foreground">
            {user.email}
          </span>
          <UserRoleBadge role={user.role} className="mt-1 w-fit" />
        </DropdownMenuLabel>
        {items.length > 0 && <DropdownMenuSeparator />}
        {items.map((item, i) => {
          const Icon = item.icon;
          return (
            <DropdownMenuItem
              key={i}
              onSelect={(e) => {
                e.preventDefault();
                item.onSelect();
              }}
            >
              {Icon ? <Icon /> : null}
              {item.label}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            void signOut();
          }}
        >
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
